/**
 * Defensive property helpers - handle mixed-shape data from imports.
 *
 * Some properties are stored with title/description/location/type as {en, ar}
 * objects, others as plain strings. These helpers normalize both shapes so the
 * UI never renders "[object Object]" and always has a reasonable fallback.
 */

/**
 * Get a localized string from a field that may be:
 *   - a {en, ar} object,
 *   - a plain string,
 *   - null / undefined.
 *
 * @param {*} value - The raw field value.
 * @param {'en'|'ar'} lang - Preferred language code.
 * @returns {string} - Always a string, never "[object Object]".
 */
export const getLocalized = (value, lang = 'en') => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    // Prefer requested language, fallback: en → ar → first available value
    if (value[lang]) return String(value[lang]);
    if (value.en) return String(value.en);
    if (value.ar) return String(value.ar);
    // Last-resort: first non-empty string value in the object
    for (const v of Object.values(value)) {
      if (typeof v === 'string' && v.trim()) return v;
    }
  }
  return '';
};

/**
 * Category-appropriate Unsplash placeholders when a property has no images.
 * Uses stable Unsplash IDs (pre-verified to resolve HTTP 200).
 */
const PLACEHOLDERS = {
  warehouse: 'https://images.unsplash.com/photo-1553413077-190dd305871c?w=1200',
  office: 'https://images.unsplash.com/photo-1497366216548-37526070297c?w=1200',
  retail: 'https://images.unsplash.com/photo-1519567241046-7f570eee3ce6?w=1200',
  showroom: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1200',
  accommodation: 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1200',
  penthouse: 'https://images.unsplash.com/photo-1600585154340-be6161a56a0c?w=1200',
  villa: 'https://images.unsplash.com/photo-1613490493576-7fde63acd811?w=1200',
  townhouse: 'https://images.unsplash.com/photo-1568605114967-8130f3a36994?w=1200',
  apartment: 'https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=1200',
  land: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1200',
  plot: 'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=1200',
  mixed: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1200',
  commercial: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=1200',
  residential: 'https://images.unsplash.com/photo-1493809842364-78817add7ffb?w=1200',
  default: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?w=1200',
};

/**
 * Pick a placeholder image URL based on property type/category tokens.
 * Priority: specific commercial > residential subtypes > commercial > default.
 */
export const getPlaceholderImage = (property) => {
  const typeStr = getLocalized(property?.type, 'en').toLowerCase();

  if (typeStr.includes('warehouse')) return PLACEHOLDERS.warehouse;
  if (typeStr.includes('office')) return PLACEHOLDERS.office;
  if (typeStr.includes('retail') || typeStr.includes('shop')) return PLACEHOLDERS.retail;
  if (typeStr.includes('showroom')) return PLACEHOLDERS.showroom;
  if (typeStr.includes('accommodation') || typeStr.includes('staff')) return PLACEHOLDERS.accommodation;
  if (typeStr.includes('penthouse')) return PLACEHOLDERS.penthouse;
  if (typeStr.includes('villa')) return PLACEHOLDERS.villa;
  if (typeStr.includes('townhouse')) return PLACEHOLDERS.townhouse;
  if (typeStr.includes('apartment')) return PLACEHOLDERS.apartment;
  if (typeStr.includes('plot') || typeStr.includes('land')) return PLACEHOLDERS.plot;
  if (typeStr.includes('mixed')) return PLACEHOLDERS.mixed;
  if (typeStr.includes('commercial')) return PLACEHOLDERS.commercial;
  if (typeStr.includes('residential')) return PLACEHOLDERS.residential;

  return PLACEHOLDERS.default;
};

/**
 * Get the primary image for a property.
 * Falls back to a category-appropriate placeholder when no images exist.
 * @returns {string} - Always a valid image URL.
 */
export const getPropertyImage = (property, index = 0) => {
  const images = Array.isArray(property?.images) ? property.images : [];
  // Filter out empty strings and known-broken upstream URLs
  const valid = images.filter(img =>
    img && typeof img === 'string' &&
    img.trim() !== '' &&
    !img.includes('hiwi-market-analysis')
  );
  if (valid.length > index) return valid[index];
  if (valid.length > 0) return valid[0];
  return getPlaceholderImage(property);
};

/**
 * Get the full image gallery (defensive): returns at least one image
 * (either the stored ones or a single placeholder).
 * @returns {string[]}
 */
export const getPropertyGallery = (property) => {
  const images = Array.isArray(property?.images) ? property.images : [];
  const valid = images.filter(img =>
    img && typeof img === 'string' &&
    img.trim() !== '' &&
    !img.includes('hiwi-market-analysis')
  );
  if (valid.length > 0) return valid;
  return [getPlaceholderImage(property)];
};

/**
 * Get the display status. Defaults to 'available' if missing/null/empty.
 * @returns {string} - One of: 'available', 'sold', 'rented', 'reserved', etc.
 */
export const getPropertyStatus = (property) => {
  const s = property?.status;
  if (s && typeof s === 'string' && s.trim()) return s;
  return 'available';
};

/**
 * Get the listing type ('sale' or 'rent'). Defaults to 'sale'.
 * @returns {string}
 */
export const getListingType = (property) => {
  const t = property?.listing_type;
  if (t === 'rent' || t === 'sale') return t;
  return 'sale';
};
